import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppContext } from 'QuoteApp/services/providers';

const INITIAL_DELAY_MS = 30_000;
const PHASE_1_INTERVAL_MS = 7_000;
const PHASE_1_DURATION_MS = 5 * 60_000;
const PHASE_2_INTERVAL_MS = 30_000;
const TOTAL_TIMEOUT_MS = 15 * 60_000;

export const useCheckoutLinkPolling = (
  active: boolean,
  onFinish?: () => void
) => {
  const { refreshDetails } = useAppContext();
  const [pollingTimedOut, setPollingTimedOut] = useState(false);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollingActiveRef = useRef(false);
  const sendTimeRef = useRef<number>(0);
  const activeStartRef = useRef<number>(0);
  const onFinishRef = useRef(onFinish);
  onFinishRef.current = onFinish;

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const scheduleNext = useCallback((poll: () => void) => {
    const now = Date.now();
    const totalElapsed = now - sendTimeRef.current;

    if (totalElapsed >= TOTAL_TIMEOUT_MS) {
      pollingActiveRef.current = false;
      setPollingTimedOut(true);
      return;
    }

    const activeElapsed = now - activeStartRef.current;
    const interval =
      activeElapsed < PHASE_1_DURATION_MS
        ? PHASE_1_INTERVAL_MS
        : PHASE_2_INTERVAL_MS;

    timerRef.current = setTimeout(poll, interval);
  }, []);

  const poll = useCallback(async () => {
    if (!pollingActiveRef.current) {
      return;
    }

    try {
      const result = await refreshDetails();
      if (result.status === 'bound') {
        pollingActiveRef.current = false;
        onFinishRef.current?.();
        return;
      }
    } catch {
      // keep polling on transient errors
    }

    if (pollingActiveRef.current) {
      scheduleNext(poll);
    }
  }, [refreshDetails, scheduleNext]);

  const startPolling = useCallback(() => {
    clearTimer();
    pollingActiveRef.current = true;
    setPollingTimedOut(false);
    sendTimeRef.current = Date.now();

    timerRef.current = setTimeout(async () => {
      if (!pollingActiveRef.current) {
        return;
      }
      activeStartRef.current = Date.now();
      await poll();
    }, INITIAL_DELAY_MS);
  }, [clearTimer, poll]);

  const resumePolling = useCallback(async () => {
    clearTimer();
    pollingActiveRef.current = true;
    setPollingTimedOut(false);
    // Do NOT reset sendTimeRef — the 15-minute total timeout is anchored to the
    // original send, not to each resume attempt.
    activeStartRef.current = Date.now();
    await poll();
  }, [clearTimer, poll]);

  useEffect(() => {
    if (active) {
      startPolling();
    }
    return () => {
      clearTimer();
      pollingActiveRef.current = false;
    };
    // startPolling is intentionally excluded: re-running it on every render
    // would restart the polling cycle. We only want to start/stop when the
    // `active` flag changes (i.e. when the policy transitions to bound).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  return { pollingTimedOut, resumePolling };
};
